import React, { FormEvent, useEffect, useRef, useState } from "react";

import "./Chat.scss";
import Thumb from "./Thumb";
import Preview from "./Preview";
import AliUpload from "./Upload";

import storageX from "../../utils/storageX";
import { createMessage, messageList, MessageListItem } from "../../api/chat";
import { CloseCircleOutline, UserOutline } from "antd-mobile-icons";
import { Modal, Toast } from "antd-mobile";
import Empty from "../../component/empty/Empty";
import { UploadFile } from "../../component/ocr-autofill/OCRUpload";
import Quick from "./Quick";

type Props = {
  readonly?: boolean
  fromDoctor: boolean;
  createUserCode: string;
  subjectCode: string;
};

const Chat: React.FC<Props> = ({ readonly = false, fromDoctor, createUserCode, subjectCode }) => {
  //
  const [userCode] = useState(() => {
    if(fromDoctor) {
      return storageX().user().userCode;
    }

    type OriginUrlParam = {
      data: string;
      from: 'EROP_PT';
      fromUrlFromH5: string;
      version: string;
    }

    const { data } = storageX().json().get('OriginUrlParam', {}) as OriginUrlParam

    if(data) {
      return JSON.parse(decodeURIComponent(data)).userCode
    }

    Modal.alert('', '系统无法获取身份信息')
    return ''
  });

  const pageSize = 20;

  // 聊天记录
  const [rows, setRows] = useState<MessageListItem[]>([]);

  const fetchingHistory = useRef(false)

  // 刷新ID
  const lastId = useRef(0);
  // 历史ID
  const lastHistoryId= useRef(0);
  // 是否有历史数据
  const [total, setTotal] = useState(0);

  // 留言
  const [content, setContent] = useState("");

  // 上传
  const [preview, setPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [urls, setUrls] = useState<string[]>([]);

  // 记录receiver 用于判断是否转移提示分隔符
  const prereceiver = useRef('')

  const removeControl = (index: number) => {
    urls.splice(index, 1);
    setUrls([...urls]);
  };

  const previewControl = (file: UploadFile) => {
    setPreview(true);
    setPreviewUrl(file.url as string);
  };

  const uploadSuccess = (url: string) => {
    setUrls((pre) => [...pre, url]);
  };

  // 发送留言
  const sendMessage = async () => {
    try {
      const { code, message } = await createMessage({
        sender: userCode,
        subjectCode: subjectCode,
        content: content,
        url: urls.join(","),
      }, fromDoctor);

      if (code === 0) {
        Toast.success("发送成功", 2);
        setContent("");
        setUrls([]);
      } else {
        Modal.alert('', message)
      }
    } catch (error) {}
  };

  const contentTyping = (event: FormEvent) => {
    setContent((event.target as HTMLInputElement).value);
  };

  // 获取更多
  const fetchHistoryMessage = async () => {
    // setLoading(true);
    const { code, message, data } = await messageList({
      id: lastHistoryId.current,
      pageSize: pageSize,
      sender: fromDoctor ? createUserCode : userCode,
      subjectCode: subjectCode,
      refresh: false,
    });

    // setLoading(false);

    if (code > 0) {
      Toast.fail(message);
      return;
    }

    if (data.rows.length === 0) {
      return;
    }

    data.rows.reverse()

    lastHistoryId.current = (data.rows[0] as MessageListItem).id;
    fetchingHistory.current = true
    setTotal(data.total);
    setRows((pre) => [...data.rows, ...pre]);

    // 修正上一个接收者编码
    prereceiver.current = rows[0].receiver

    // 加载历史记录停止自动滚动
    let elHeight = 0;
    const el = document.getElementById('chat-records');
    const lis = function(){
      if(el) {
        const { height } = (el.firstElementChild as HTMLDivElement).getBoundingClientRect()
        if(fetchingHistory.current && el.scrollTop > height - elHeight) {
          el.removeEventListener('scrollend', lis)
          fetchingHistory.current = false;
        }
      }
    }
    
    if (el) {
      elHeight = el.getBoundingClientRect().height + 100
      // 防止多次绑定
      el.removeEventListener('scrollend', lis)
      el.addEventListener('scrollend', lis)
    }
  };

  // 获取最新消息
  const fetchMessage = async (refreshCode: string, refresh=true) => {
    // setLoading(true);
    const { code, message, data } = await messageList({
      id: lastId.current,
      pageSize: pageSize,
      // 医生使用创建者编码
      sender: fromDoctor ? createUserCode : userCode,
      subjectCode: refreshCode,
      refresh: refresh,
    });
    // setLoading(false);

    if (code !== 0) {
      Toast.fail(message);
      return;
    }

    if (data.rows.length === 0) {
      return;
    }

    const messages = data.rows;

    if(lastId.current === 0) {
      messages.reverse();
    }

    lastId.current = (messages[messages.length-1] as MessageListItem).id;
    lastHistoryId.current = (messages[0] as MessageListItem).id;

    setTotal(data.total);
    setRows((pre) => [...pre, ...messages]);

    // 非初始状态
    if(prereceiver.current !== '' && rows.length > 0) {
      prereceiver.current = rows[rows.length-1].receiver
    }

    // 用户没有点击加载更多时，自动滚动到底
    if(!fetchingHistory.current) {
      setTimeout(()=> {
        const el = document.getElementById('chat-records');
        if (el) {
          const { height } = (el.firstElementChild as HTMLDivElement).getBoundingClientRect();
          el.scrollTo({
            top: height + 50,
            behavior: "smooth",
          });
        }
      }, 300)
    }
  }

  useEffect(()=> {
    fetchMessage(subjectCode, false);

    const timer = setInterval(() => fetchMessage(subjectCode), 5000);

    return () => {
      clearInterval(timer);
    };
  }, [])

  return (
    <>
      <div className="feat-message--main">
        <h2 className="feat-message--name">{fromDoctor ? '与患者对话' : '与医生对话'}</h2>
        {
          !fromDoctor && (
            <div className="feat-message--tips">本系统支持留言功能，您的信息我已知晓，我会在看到信息后尽快回复您，如您情况比较着急，您也可以到当地医院向医生咨询了解。</div>
          )
        }
        <div className="feat-message--chat customized-scrollbar" id='chat-records'>
          {rows.length > 0 ? (
            <ul>
              {rows.length < total && (
                <li className="feat-message--divider">
                  <div className="inner">
                    <span onClick={fetchHistoryMessage} style={{cursor: 'pointer'}}>点击加载更多</span>
                  </div>
                </li>
              )}
              
              {rows.map((t, i) => {
                // 转移分隔符
                let transout = false;
                if(i > 0) {
                  if('' !== prereceiver.current) {
                    if(prereceiver.current != t.receiver) {
                      transout = true;
                    }
                  }
                }

                prereceiver.current = t.receiver

                return (
                  <li key={i} className={t.direction === 0 ? 'mine' : ''}>
                    {transout && <div className="feat-message--transout"><span>以下是转移记录</span></div>}
                    {transout && <div className="feat-message--placeholder"></div>}
                    <div className="feat-message--avatar">
                      <UserOutline />
                    </div>
                    <div
                      data-content={`${t.createTime.split('T').join(' ')} ${t.direction === 1 ? t.receiver : ''}`}
                      className="feat-message--content"
                    >
                    <p dangerouslySetInnerHTML={{__html: t.content.replaceAll("\n", '<br/>')}}></p>
                    {t.url.length > 0 && (
                      <Thumb
                        closable={false}
                        onPreview={previewControl}
                        urls={t.url.split(",")}
                      />
                    )}
                    </div>
                  </li>
                )
              })}
            </ul>
          ) : (
            <Empty description="暂无聊天记录"/>
          )}
        </div>
        <div className="feat-message--message">
          <div className="feat-message--file">
            <AliUpload disabled={urls.length > 0} onUpload={uploadSuccess} />
          </div>
          <div className="feat-message--typing">
            <input
              onInput={contentTyping}
              value={content}
              placeholder="请输入留言内容"
              maxLength={500}
            />
            <button onClick={sendMessage} type="button" className="send">发送</button>
            <button onClick={()=> setContent('')} type="button" className="clear">
              <CloseCircleOutline/>
            </button>
            {(fromDoctor && content.length === 0) && <Quick onConfirm={(value: string)=> setContent(value)}/>}
          </div>
          <Thumb
            onRemove={removeControl}
            onPreview={previewControl}
            urls={urls}
            className="feat-message--thumbs"
          />

          {
            readonly && (
              <div className="feat-message--readonly"></div>
            )
          }
        </div>
      </div>
      <Preview
        close={() => setPreview(false)}
        visible={preview}
        url={previewUrl}
      />
    </>
  );
};

export default Chat;
